import { useState } from "react";
import { useStore, CAT_META } from "../store/Store";

// ─── SHOWCASE CARD ────────────────────────────────────────────────────────────
// Used only in the homepage carousels (HSection). A bold color block per
// collection (using that collection's own accent), a big label, and the
// product photo sitting in a soft organic "blob" frame — energetic, catalog
// style, but in Matchwell's own palette (not anyone else's branding).
// Sized generously (Flipkart-scale) and dressed up with a gold divider and
// editorial serif type — meant to read as ladies-fashion, not generic retail.
export default function ShowcaseCard({ p, onNav, accent }) {
  const { dispatch, wish, toggleWish, notify } = useStore();
  const [hovered, setHovered] = useState(false);
  const meta = CAT_META[p.category] || { emoji:"👗" };
  const disc = Math.round((1 - p.price / p.mrp) * 100);
  const wished = wish.includes(p.id);

  const addCart = (e) => {
    e.stopPropagation();
    dispatch({ type:"ADD", p:{ ...p, sc:p.colors?.[0]||"Default", ss:p.sizes?.[0]||"Free" } });
    notify(`${p.name} added!`);
    if (navigator.vibrate) navigator.vibrate(50);
  };

  return (
    <div
      onClick={()=>onNav("product", p.id)}
      onMouseEnter={()=>setHovered(true)}
      onMouseLeave={()=>setHovered(false)}
      style={{
        borderRadius:26, overflow:"hidden", cursor:"pointer", position:"relative",
        background:`linear-gradient(160deg, ${accent} 0%, ${accent}b8 100%)`,
        boxShadow: hovered ? `0 26px 52px ${accent}55` : `0 10px 26px ${accent}38`,
        transform: hovered ? "translateY(-8px)" : "translateY(0)",
        transition:"all 0.4s cubic-bezier(.2,.8,.2,1)",
      }}>

      {/* Top label zone — the product's own name, big and editorial */}
      <div style={{ padding:"24px 24px 10px" }}>
        <div style={{ fontSize:10, fontWeight:700, letterSpacing:2.5, color:"rgba(255,255,255,0.8)", textTransform:"uppercase", marginBottom:5 }}>
          {meta.emoji} {p.category}
        </div>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:900, color:"white", letterSpacing:0.2, lineHeight:1.15, textShadow:"0 2px 12px rgba(0,0,0,0.15)", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden", minHeight:"2.3em" }}>
          {p.name}
        </div>
        {/* thin gold rule — small editorial touch */}
        <div style={{ width:38, height:2.5, background:"linear-gradient(90deg,#F5D67A,transparent)", borderRadius:2, marginTop:10 }}/>
      </div>

      {/* Photo — wave-cut top edge frame (SVG mask), sitting inside the color block */}
      <div style={{ position:"relative", height:320, padding:"0 22px" }}>
        <div style={{
          width:"100%", height:"100%",
          overflow:"hidden", background:"white",
          WebkitMaskImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' preserveAspectRatio='none'%3E%3Cpath d='M0,22 C18,4 34,4 50,16 C66,28 82,28 100,10 L100,100 L0,100 Z'/%3E%3C/svg%3E\")",
          maskImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' preserveAspectRatio='none'%3E%3Cpath d='M0,22 C18,4 34,4 50,16 C66,28 82,28 100,10 L100,100 L0,100 Z'/%3E%3C/svg%3E\")",
          WebkitMaskSize:"100% 100%", maskSize:"100% 100%",
          WebkitMaskRepeat:"no-repeat", maskRepeat:"no-repeat",
          transition:"transform 0.5s cubic-bezier(.2,.8,.2,1)",
          transform: hovered ? "scale(1.03)" : "scale(1)",
        }}>
          {p.img
            ? <img src={p.img} alt={p.name} style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top" }} loading="lazy"/>
            : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:64, opacity:0.25, background:meta.light||"#fafafa" }}>{meta.emoji}</div>
          }
        </div>

        {disc > 0 && (
          <span style={{ position:"absolute", top:14, right:34, background:"#1a1a2e", color:"#F5D67A", fontSize:11, fontWeight:800, padding:"5px 13px", borderRadius:20, boxShadow:"0 6px 16px rgba(0,0,0,0.28)", letterSpacing:0.3 }}>
            {disc}% OFF
          </span>
        )}

        <button onClick={e=>{ e.stopPropagation(); toggleWish(p.id); if(navigator.vibrate)navigator.vibrate(30); }}
          style={{ position:"absolute", top:14, left:34, background:"white", border:"none", borderRadius:"50%", width:32, height:32, fontSize:15, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 12px rgba(0,0,0,0.22)", transform:wished?"scale(1.15)":"scale(1)", transition:"transform 0.2s" }}>
          {wished ? "❤️" : "🤍"}
        </button>
      </div>

      {/* Bottom info panel — price only, no repeated name */}
      <div style={{ background:"white", borderRadius:"22px 22px 0 0", marginTop:14, padding:"16px 20px 20px", position:"relative" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
          <div>
            <span style={{ fontSize:20, fontWeight:900, color:"#1a1a2e" }}>₹{p.price?.toLocaleString("en-IN")}</span>
            {p.mrp > p.price && <span style={{ fontSize:12, color:"#aaa", textDecoration:"line-through", marginLeft:7 }}>₹{p.mrp?.toLocaleString("en-IN")}</span>}
          </div>
          {p.stock > 0 ? (
            <button onClick={addCart}
              style={{ background:accent, color:"white", border:"none", padding:"9px 18px", borderRadius:22, fontSize:11.5, fontWeight:800, cursor:"pointer", whiteSpace:"nowrap", boxShadow:`0 6px 16px ${accent}44` }}>
              + Add
            </button>
          ) : (
            <span style={{ fontSize:10, fontWeight:700, color:"#EF4444" }}>Out of Stock</span>
          )}
        </div>
      </div>
    </div>
  );
}

